import React, { useState, useRef } from 'react'
import Colors from 'colors.json'

import DatetimePickerInput from 'components/DatetimePickerInput'
import Input from 'components/Input'
import Button from 'components/Button'
import Dropdown from 'components/Dropdown'
import Modal from 'components/Modal'
import { Form, Formik, FormikProps } from 'formik'

import { postGetTraderListAPI, postBulkMemberUpdateAPI } from 'api/bulk-transactions'
import { BulkTransactionsTxSchemas } from 'schemas/bulkTransactionsTxSchemas'
import { BulkTransactionsTxFormTypes } from 'types/bulkTransactionsTXTypes'
import useMemberStore from 'store/member'
import { useBulkTransactionsTx } from './useBulkTransactionsTx'
import MerchantLookupTx from 'views/MemberProfile/ManualTx/MerchantLookupTx'
import BatchLookupTx from '../BatchLookupTx'
import { toast } from 'react-toastify'

import { MANUAL_TRANSACTION_TYPES } from 'constants/manualTransactionTypes'

import { ACCESS_CONFIG } from 'setups/config'

import { isGenericClient } from 'setups/controller'

const Index: React.FC<{ onCloseModal: () => void }> = ({
  onCloseModal,
}): React.ReactElement => {
  // STATES
  const { initialValues, handleSubmitBulkTransactionsTx } =
    useBulkTransactionsTx(onCloseModal)

  const [startCardNumber, setStartCardNumber] = useState('')
  const [endCardNumber, setEndCardNumber] = useState('')
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [showTraderCount, setShowTraderCount] = useState(false)
  const [showLimitMsg, setShowLimitMsg] = useState(false)
  const [traderCount, setTraderCount] = useState(0)

  const formRef = useRef<FormikProps<BulkTransactionsTxFormTypes>>(null)

  const projectClient = process.env.REACT_APP_PROJ

  const MANUAL_TRANSACTION_TYPES_OPTIONS = React.useMemo(() => {
    const clientConfig = ACCESS_CONFIG.application.clients.find(
      (client) => client.name === projectClient?.toLowerCase()
    )

    if (!clientConfig) return []

    const excludedTransactions =
      clientConfig.setup
        .find((setup) => setup.module === 'member')
        ?.exclude.map((ex) => ex.name) || []

    return MANUAL_TRANSACTION_TYPES.filter(
      (type) => !excludedTransactions.includes(type.name.toLowerCase())
    ).map((item) => ({
      label: item.name,
      value: item.transactionTypeId,
    }))
  }, [projectClient])

  const getMemberStatuses = useMemberStore((state) => state.getMemberStatuses)
  const memberStatuses = useMemberStore((state) => state.memberStatuses)

  return (
    <>
      <Formik<BulkTransactionsTxFormTypes>
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={BulkTransactionsTxSchemas}
        onSubmit={handleSubmitBulkTransactionsTx}
      >
        {({ values, isSubmitting, setFieldValue, validateForm }) => {
          return (
            <Form className="flex flex-col">
              <div className="mb-5 border-solid border-gray-400 border p-5">
                <MerchantLookupTx
                  isMerchantLookupToggle={true}
                  setFieldValue={setFieldValue}
                  useMerchantLookupLists={true}
                />
                <div className="flex gap-4 items-center">
                  <Input
                    name="fromTraderId"
                    label="From Trader ID"
                    disabled
                    containerClassName="w-full"
                  />
                  <Input
                    name="deviceCode"
                    label="Device Code"
                    disabled
                    containerClassName="w-full"
                    type="number"
                  />
                </div>
                <DatetimePickerInput
                  title={'Transaction Date'}
                  showTimeSelect={false}
                  setFieldName="transactionDate"
                  setFieldValue={setFieldValue}
                  className="mb-0 w-48"
                />
              </div>
              <div className="mb-5 border-solid border-gray-400 border p-5">
                <BatchLookupTx
                  isBatchLookupToggle={true}
                  setFieldValue={setFieldValue}
                />
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4 items-center">
                    <Input
                      name="startCardNumber"
                      label="Ident Start"
                      containerClassName="w-full"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setStartCardNumber(e.target.value)
                        setFieldValue('startCardNumber', e.target.value)
                      }}
                      value={values.startCardNumber || ''}
                    />
                    <Input
                      name="endCardNumber"
                      label="Ident End"
                      containerClassName="w-full"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setEndCardNumber(e.target.value)
                        setFieldValue('endCardNumber', e.target.value)
                      }}
                      value={values.endCardNumber || ''}
                    />
                    <Button
                      type="button"
                      className="rounded font-bold"
                      onClick={async () => {
                        try {
                          const response = await postGetTraderListAPI({
                            startCardNumber: values.startCardNumber,
                            endCardNumber: values.endCardNumber,
                            batchId: values.batchId ?? 0,
                          })

                          const count = response.data?.count ?? 0
                          const traderList = response.data?.traderList ?? []

                          if (count >= 0 && count < 101) {
                            setShowTraderCount(true)
                            setShowLimitMsg(false)
                            setFieldValue(
                              'toTraderIds',
                              traderList.map(
                                (trader: { traderId: number }) =>
                                  trader.traderId
                              )
                            )
                            // Fetch member statuses using the first trader's ID
                            if (traderList.length > 0) {
                              getMemberStatuses(String(traderList[0].traderId))
                            }
                            setTraderCount(count)
                          } else {
                            setShowTraderCount(false)
                            setShowLimitMsg(true)
                          }
                        } catch (error) {
                          toast.error('Failed to fetch trader list')
                        }
                      }}
                    >
                      Show Card Count
                    </Button>
                  </div>
                </div>
                {showTraderCount && (
                  <div style={{ color: 'green', marginBottom: '20px', fontStyle: 'italic' }}>
                    There are {traderCount} cards currently selected.
                  </div>
                )}
                {showLimitMsg && (
                  <div style={{ color: 'red', marginBottom: '20px', fontStyle: 'italic' }}>
                    You can select up to 100 cards. Please narrow down the card number range.
                  </div>
                )}
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4 items-end">
                    <Dropdown
                      title="Update To Trader Status"
                      options={memberStatuses.map((status) => ({
                        label: status.statusName,
                        value: String(status.statusId),
                      }))}
                      borderColor="#C4C4C4"
                      onSelect={(item) => {
                        setFieldValue('traderStatusId', Number(item.value))
                      }}
                      containerClassName="w-64"
                    />
                    <Button
                      type="button"
                      className="rounded font-bold"
                      onClick={async () => {
                        try {
                          await postBulkMemberUpdateAPI({
                            toTraderIds: values.toTraderIds,
                            traderStatusId: values.traderStatusId ?? 0,
                          })
                          toast.success(`Trader Status successfully updated!`)
                        } catch (error) {
                          toast.error('Failed to udpate trader status')
                        }
                      }}
                    >
                      Update
                    </Button>
                  </div>
                </div>
              </div>
              <div className="mb-5 border-solid border-gray-400 border p-5">
                <Dropdown
                  title="Transaction Type"
                  options={MANUAL_TRANSACTION_TYPES_OPTIONS.map((option) => ({
                    ...option,
                    value: String(option.value),
                  }))}
                  className="w-64 tracking-wider mb-5"
                  borderColor="#C4C4C4"
                  onSelect={(data) =>
                    setFieldValue('transactionTypeId', Number(data.value))
                  }
                />
                <div className="flex gap-4 items-center">
                  <Input
                    type="number"
                    name="purchaseValue"
                    label="Purchase Value"
                    disabled={
                      ![1, -1, 2, -2, 4, -4].includes(Number(values.transactionTypeId))
                    }
                    containerClassName="w-full mb-0"
                  />
                  <Input
                    type="number"
                    name="loyaltyValue"
                    label="Loyalty Value"
                    disabled={
                      ![21, 31, 80, -81].includes(
                        Number(values.transactionTypeId)
                      )
                    }
                    containerClassName="w-full mb-0"
                  />
                  <Input
                    type="number"
                    name="creditValue"
                    label="Credit Value"
                    disabled={
                      ![4, -4, 9, -9, 20, 24, 30, 34].includes(
                        Number(values.transactionTypeId)
                      )
                    }
                    containerClassName="w-full mb-0"
                  />
                  <Input
                    type="number"
                    name="voucherValue"
                    label="Voucher Value"
                    disabled={!isGenericClient(projectClient)}
                    containerClassName="w-full mb-0"
                  />
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <Input
                  type="input"
                  name="comments"
                  label="Comment"
                  containerClassName="w-full"
                />
              </div>
              <footer className="flex justify-end pb-4 pl-4 pr-4">
                <Button
                  type="button"
                  className="mr-2 rounded bg-primary"
                  disabled={isSubmitting}
                  onClick={async () => {
                    const errors = await validateForm()
                    if (Object.keys(errors).length === 0) {
                      // check if traderCount is 0
                      if (!values.toTraderIds?.length) {
                        toast.error('Please fetch cards first.')
                        return
                      }
                      setShowConfirmationModal(true)
                    } else {
                      formRef.current?.submitForm() // force validation errors to show
                    }
                  }}
                >
                  <span className="tracking-wider font-bold">Submit</span>
                </Button>
                <Button
                  className="bg-white border rounded border-primary"
                  onClick={onCloseModal}
                  style={{ color: Colors.primary }}
                >
                  <span className="tracking-wider font-bold">Cancel</span>
                </Button>
              </footer>
            </Form>
          )
        }}
      </Formik>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        title="Confirm Transaction"
        noFooter
      >
        <div className="p-6">
          <p className="mb-6">
            You are about to issue transactions to{' '}
            <strong>{traderCount}</strong> traders. Are you sure you want to
            proceed?
          </p>
          <div className="flex justify-end gap-4">
            <Button
              onClick={() => {
                setShowConfirmationModal(false)
                formRef.current?.submitForm()
              }}
              className="rounded bg-primary tracking-wider font-bold"
            >
              Yes
            </Button>
            <Button
              onClick={() => setShowConfirmationModal(false)}
              className="rounded bg-white border border-primary tracking-wider font-bold"
              style={{ color: 'black' }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default Index
